import './index.css';
import { jsx } from 'react/jsx-runtime';
import { useRef, useState, useContext, useMemo, useCallback, useEffect, useLayoutEffect, Fragment } from 'react';
import { initScrollHandlers } from '@hh.ru/magritte-internal-custom-scroll';
import { AsyncDataContext } from './AsyncDataContext.js';
import { s as styles } from './date-picker-DxEXPS6T.js';

const InfiniteScroll = ({ initialRenderedItems, invisibleThreshold, visibleThreshold, makeItemFunc, renderItemFunc, onVisibleItemChange, }) => {
    const stateRef = useRef({
        items: new Map([...Array(initialRenderedItems).keys()].map((index) => [index, makeItemFunc(index)])),
        firstRenderedKey: 0,
        lastRenderedKey: initialRenderedItems - 1,
        needScrollCorrection: false,
    });
    const [stateKey, setStateKey] = useState(0);
    const asyncDataContext = useContext(AsyncDataContext);
    const boundariesRef = useRef(null);
    const itemElementsRef = useRef(new Map());
    const translateYRef = useRef(0);
    const visibleAreaMetricsRef = useRef(null);
    const visibleItemKeyRef = useRef(0);
    const wrapperRef = useRef(null);
    const itemRefCallbacks = useMemo(() => {
        return new Map([...stateRef.current.items.entries()].map(([key]) => {
            const callback = ((ref) => {
                if (ref !== null) {
                    itemElementsRef.current.set(key, ref);
                }
            });
            return [key, callback];
        }));
    }, [stateKey]); // eslint-disable-line disable-autofix/react-hooks/exhaustive-deps
    const addItemsIfNeeded = useCallback((scrollDirection) => {
        if (!visibleAreaMetricsRef.current) {
            return;
        }
        const { items, firstRenderedKey, lastRenderedKey } = stateRef.current;
        // скролл вверх
        if (scrollDirection === 'up') {
            // самый верхний видимый айтем близко к верхнему краю экрана — нужно рендерить еще один сверху
            const firstRenderedItemElement = itemElementsRef.current.get(firstRenderedKey);
            if (firstRenderedItemElement) {
                const firstRenderedItemMetrics = firstRenderedItemElement.getBoundingClientRect();
                if (visibleAreaMetricsRef.current.top - firstRenderedItemMetrics.top < invisibleThreshold) {
                    if (!items.has(firstRenderedKey - 1)) {
                        stateRef.current.items = new Map([
                            [firstRenderedKey - 1, makeItemFunc(firstRenderedKey - 1)],
                            ...items.entries(),
                        ]);
                        stateRef.current.needScrollCorrection = true;
                    }
                    stateRef.current.firstRenderedKey = firstRenderedKey - 1;
                    stateRef.current.lastRenderedKey = lastRenderedKey - 1;
                    setStateKey(Math.random());
                }
            }
            // последний пересекший верхнюю границу видимой области запоминаем как visible item
            for (let key = firstRenderedKey; key < lastRenderedKey; key += 1) {
                if (stateRef.current.items.has(key) && itemElementsRef.current.has(key)) {
                    const itemElement = itemElementsRef.current.get(key);
                    if (itemElement.getBoundingClientRect().bottom - visibleAreaMetricsRef.current.top >
                        visibleThreshold) {
                        if (key !== visibleItemKeyRef.current) {
                            onVisibleItemChange(stateRef.current.items.get(key), key);
                            visibleItemKeyRef.current = key;
                        }
                        break;
                    }
                }
            }
        }
        // скролл вниз
        if (scrollDirection === 'down') {
            // самый нижний видимый айтем близко к нижнему краю экрана — нужно рендерить еще один снизу
            const lastRenderedItemElement = itemElementsRef.current.get(lastRenderedKey);
            if (lastRenderedItemElement) {
                const lastRenderedItemMetrics = lastRenderedItemElement.getBoundingClientRect();
                if (lastRenderedItemMetrics.bottom - visibleAreaMetricsRef.current.bottom < invisibleThreshold) {
                    if (!items.has(lastRenderedKey + 1)) {
                        stateRef.current.items = new Map([
                            ...items.entries(),
                            [lastRenderedKey + 1, makeItemFunc(lastRenderedKey + 1)],
                        ]);
                    }
                    stateRef.current.firstRenderedKey = firstRenderedKey + 1;
                    stateRef.current.lastRenderedKey = lastRenderedKey + 1;
                    setStateKey(Math.random());
                }
            }
            // последний пересекший нижнюю границу видимой области запоминаем как visible item
            for (let key = lastRenderedKey; key > firstRenderedKey; key -= 1) {
                if (stateRef.current.items.has(key) && itemElementsRef.current.has(key)) {
                    const itemElement = itemElementsRef.current.get(key);
                    if (visibleAreaMetricsRef.current.bottom - itemElement.getBoundingClientRect().top >
                        visibleThreshold) {
                        if (key !== visibleItemKeyRef.current) {
                            onVisibleItemChange(stateRef.current.items.get(key), key);
                            visibleItemKeyRef.current = key;
                        }
                        break;
                    }
                }
            }
        }
    }, [invisibleThreshold, visibleThreshold, makeItemFunc, onVisibleItemChange, setStateKey]);
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return void 0;
        }
        const handleTouchMove = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        wrapper.addEventListener('touchmove', handleTouchMove);
        return () => wrapper.removeEventListener('touchmove', handleTouchMove);
    }, []);
    useEffect(() => {
        return initScrollHandlers({
            axis: 'vertical',
            wrapperRef,
            onTouchMove: (event) => {
                if (!wrapperRef.current) {
                    return;
                }
                // если достигнут лимит лоадеров, не даем скроллить за границы первого и последнего айтема
                if (asyncDataContext !== null && asyncDataContext.isLimitExceeded() && boundariesRef.current !== null) {
                    const translateY = translateYRef.current + event.delta;
                    if (translateY > boundariesRef.current.bottom && translateY < boundariesRef.current.top) {
                        translateYRef.current = translateY;
                    }
                }
                else {
                    translateYRef.current += event.delta;
                    addItemsIfNeeded(event.delta > 0 ? 'up' : 'down');
                }
                wrapperRef.current.style.transform = `translateY(${translateYRef.current}px)`;
            },
        });
    }, [addItemsIfNeeded]); // eslint-disable-line disable-autofix/react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        if (!visualViewport || !wrapperRef.current) {
            return;
        }
        if (!visibleAreaMetricsRef.current) {
            visibleAreaMetricsRef.current = { top: wrapperRef.current.offsetTop, bottom: visualViewport.height };
        }
        const { firstRenderedKey, lastRenderedKey, needScrollCorrection } = stateRef.current;
        const firstRenderedItemElement = itemElementsRef.current.get(firstRenderedKey);
        const lastRenderedItemElement = itemElementsRef.current.get(lastRenderedKey);
        if (firstRenderedItemElement && lastRenderedItemElement) {
            // если мы добавили айтем сверху, чтобы скролл не прыгал, компенсируем его на высоту самого верхнего айтема
            if (needScrollCorrection) {
                stateRef.current.needScrollCorrection = false;
                translateYRef.current -= firstRenderedItemElement.clientHeight;
                if (boundariesRef.current !== null) {
                    boundariesRef.current.top -= firstRenderedItemElement.clientHeight;
                }
                wrapperRef.current.style.transform = `translateY(${translateYRef.current}px)`;
            }
            // если достигнут лимит лоадеров, запоминаем границы первого и последнего айтема и не даем скроллить за них
            if (asyncDataContext !== null && !asyncDataContext.isLimitExceeded()) {
                const top = translateYRef.current +
                    visibleAreaMetricsRef.current.top -
                    firstRenderedItemElement.getBoundingClientRect().top;
                const bottom = translateYRef.current +
                    visibleAreaMetricsRef.current.bottom -
                    lastRenderedItemElement.getBoundingClientRect().bottom;
                boundariesRef.current = { top, bottom };
            }
        }
    }, [stateKey]); // eslint-disable-line disable-autofix/react-hooks/exhaustive-deps
    return (jsx("div", { className: styles.infiniteScrollWrapper, ref: wrapperRef, children: [...stateRef.current.items.entries()].map(([key, item]) => (jsx(Fragment, { children: key >= stateRef.current.firstRenderedKey && key <= stateRef.current.lastRenderedKey ? (jsx("div", { ref: itemRefCallbacks.get(key), children: renderItemFunc(item) })) : (jsx("div", { style: { height: `${itemElementsRef.current.get(key)?.clientHeight}px` } })) }, `${item}`))) }));
};

export { InfiniteScroll };
//# sourceMappingURL=InfiniteScroll.js.map
